import {
  Box,
  Circle,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  Badge,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { CalendarIcon } from "@chakra-ui/icons";
import { forwardRef, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";
import {
  FinancialItemViewModel,
  FinancialViewModel,
} from "../../../models/Financial";
import { ProcessViewModel } from "../../../models/Process";
import { crm } from "../../../theme";

export interface TimelinePayment {
  process: ProcessViewModel;
  financialItem: FinancialItemViewModel;
  parcelLabel: string;
  displayAmount: string;
  amount: number;
  isPaid: boolean;
  statusLabel: string;
}

export interface TimelineDay {
  dateKey: string;
  date: DateTime;
  payments: TimelinePayment[];
  totalAmount: number;
}

const parsePaymentAmount = (valor?: string): number => {
  if (!valor || valor.length <= 1) return 0;
  const numPart = valor.replace(/[^\d,.-]/g, "").trim();
  if (!numPart) return 0;
  if (numPart.includes(",") && numPart.includes(".")) {
    return parseFloat(numPart.replace(/\./g, "").replace(",", ".")) || 0;
  }
  if (numPart.includes(",")) {
    return parseFloat(numPart.replace(",", ".")) || 0;
  }
  return parseFloat(numPart) || 0;
};

const getPaymentDate = (item: FinancialItemViewModel): string | undefined =>
  item.dataPagamento || item.dataCambio;

const isPaymentPaid = (item: FinancialItemViewModel): boolean =>
  (!!item.dataCambio && !!item.taxaCambio) ||
  item.contratoCambio === "FINIMP";

const getPaymentStatus = (item: FinancialItemViewModel): string => {
  if (isPaymentPaid(item)) return "Pago";
  if (item.dataPagamento) return "Programado";
  return "Ag. programação";
};

export const buildFinancialTimeline = (
  page: FinancialViewModel,
): TimelineDay[] => {
  const byDate = new Map<string, TimelinePayment[]>();

  (page?.processes ?? []).forEach((process) => {
    (process.financialItems ?? [])
      .filter((f) => (f.valorPagamento?.length ?? 0) > 1)
      .forEach((item, index) => {
        const dateIso = getPaymentDate(item);
        if (!dateIso) return;

        const date = DateTime.fromISO(dateIso);
        if (!date.isValid) return;

        const dateKey = date.toFormat("yyyy-LL-dd");
        const payment: TimelinePayment = {
          process,
          financialItem: item,
          parcelLabel: index === 0 ? "Sinal" : "Saldo",
          displayAmount: item.valorPagamento ?? "",
          amount: parsePaymentAmount(item.valorPagamento),
          isPaid: isPaymentPaid(item),
          statusLabel: getPaymentStatus(item),
        };

        const existing = byDate.get(dateKey) ?? [];
        existing.push(payment);
        byDate.set(dateKey, existing);
      });
  });

  return Array.from(byDate.entries())
    .map(([dateKey, payments]) => ({
      dateKey,
      date: DateTime.fromISO(dateKey),
      payments,
      totalAmount: payments.reduce((sum, p) => sum + p.amount, 0),
    }))
    .filter((day) => day.date.year >= 2000)
    .sort((a, b) => a.date.toMillis() - b.date.toMillis());
};

interface FinancialTimelineProps {
  page: FinancialViewModel;
  currency: string;
  loading?: boolean;
  onOpenProcess: (process: ProcessViewModel) => void;
}

const getCurrentMonthStart = () => DateTime.now().startOf("month").toJSDate();

const TimelineMonthInput = forwardRef<
  HTMLInputElement,
  { value?: string; onClick?: () => void }
>(({ value, onClick }, ref) => (
  <InputGroup w={{ base: "100%", md: "180px" }} bg="white">
    <Input
      ref={ref}
      readOnly
      value={value}
      onClick={onClick}
      cursor="pointer"
      bg="white"
      textTransform="capitalize"
    />
    <InputRightElement pointerEvents="none">
      <CalendarIcon color="gray.500" />
    </InputRightElement>
  </InputGroup>
));
TimelineMonthInput.displayName = "TimelineMonthInput";

const FinancialTimeline = ({
  page,
  currency,
  loading,
  onOpenProcess,
}: FinancialTimelineProps) => {
  const [selectedMonth, setSelectedMonth] = useState<Date>(getCurrentMonthStart);

  const formatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency ?? "USD",
  });

  const periodLabel = DateTime.fromJSDate(selectedMonth)
    .setLocale("pt-BR")
    .toFormat("LLLL yyyy");

  const days = useMemo(() => {
    const period = DateTime.fromJSDate(selectedMonth).startOf("month");
    const periodEnd = period.endOf("month");

    return buildFinancialTimeline(page).filter(
      (day) => day.date >= period && day.date <= periodEnd,
    );
  }, [page, selectedMonth]);

  if (loading) return null;

  const minTimelineWidth = Math.max(days.length * 168, 360);

  return (
    <Box
      w="100%"
      p={{ base: "24px 16px", md: "32px" }}
      mb="32px"
      backgroundColor="white"
      borderRadius="12px"
      border={`1px solid ${crm.border}`}
      boxShadow={crm.shadowSm}
    >
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        flexDir={{ base: "column", md: "row" }}
        gap={4}
        mb="24px"
      >
        <Heading size="md" fontWeight="normal">
          Linha do tempo de pagamentos
        </Heading>
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb="8px">
            Período
          </Text>
          <DatePicker
            selected={selectedMonth}
            onChange={(date: Date | null) => {
              if (date) {
                setSelectedMonth(
                  DateTime.fromJSDate(date).startOf("month").toJSDate(),
                );
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            locale="pt-br"
            customInput={<TimelineMonthInput />}
          />
        </Box>
      </Flex>

      {days.length === 0 ? (
        <Text color="gray.500">
          Nenhum pagamento com data definida em {periodLabel}.
        </Text>
      ) : (
      <Box w="100%" overflowX="auto" pb="8px">
        <Box
          position="relative"
          w="100%"
          minW={`${minTimelineWidth}px`}
          minH="150px"
          px="16px"
        >
          <Box
            position="absolute"
            left="32px"
            right="32px"
            top="52px"
            h="3px"
            bg={crm.brand}
            borderRadius="full"
            zIndex={0}
          />

          <Flex
            position="relative"
            zIndex={1}
            w="100%"
            justify={days.length === 1 ? "center" : "space-between"}
            align="flex-start"
            gap={days.length === 1 ? 0 : 2}
          >
            {days.map((day) => {
              const pendingCount = day.payments.filter((p) => !p.isPaid).length;

              return (
                <Flex
                  key={day.dateKey}
                  flexDir="column"
                  align="center"
                  flex={days.length === 1 ? "0 0 auto" : "0 1 auto"}
                  minW="100px"
                  maxW="180px"
                  textAlign="center"
                >
                  <PaymentDayDetail
                    day={day}
                    formatter={formatter}
                    onOpenProcess={onOpenProcess}
                  />

                  <Circle
                    size="14px"
                    bg="white"
                    borderWidth="3px"
                    borderColor={crm.brand}
                    mt="36px"
                    flexShrink={0}
                  />

                  <Text
                    mt="10px"
                    fontSize="sm"
                    fontWeight="semibold"
                    whiteSpace="nowrap"
                  >
                    {day.date.toFormat("dd/LL/yyyy")}
                  </Text>
                  {pendingCount > 0 && pendingCount < day.payments.length && (
                    <Text fontSize="xs" color="gray.500">
                      {pendingCount} a pagar
                    </Text>
                  )}
                </Flex>
              );
            })}
          </Flex>
        </Box>
      </Box>
      )}
    </Box>
  );
};

const PaymentDayDetail = ({
  day,
  formatter,
  onOpenProcess,
}: {
  day: TimelineDay;
  formatter: Intl.NumberFormat;
  onOpenProcess: (process: ProcessViewModel) => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const hasPending = day.payments.some((p) => !p.isPaid);
  const totalLabel =
    day.payments.length === 1
      ? day.payments[0].displayAmount
      : formatter.format(day.totalAmount);

  return (
    <>
      <Button
        type="button"
        variant="link"
        color={hasPending ? crm.danger : crm.success}
        fontWeight="semibold"
        fontSize="md"
        whiteSpace="nowrap"
        cursor="pointer"
        zIndex={2}
        position="relative"
        _hover={{ textDecoration: "underline" }}
        onClick={onOpen}
      >
        {totalLabel}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent fontFamily="Inter">
          <ModalHeader>
            Pagamentos em {day.date.toFormat("dd/LL/yyyy")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={3}>
              {day.payments.map((payment, idx) => (
                <Box
                  key={`${payment.process.id}-${payment.financialItem.intId}-${idx}`}
                  p={3}
                  borderWidth="1px"
                  borderRadius="8px"
                  borderColor={crm.border}
                >
                  <Flex justify="space-between" align="flex-start" gap={2}>
                    <Box>
                      <Text fontWeight="semibold" fontSize="sm">
                        Processo {payment.process.cvfRef}
                      </Text>
                      <Text fontSize="sm" color={crm.textMuted}>
                        {payment.parcelLabel} · {payment.displayAmount}
                      </Text>
                    </Box>
                    <Badge
                      bg={payment.isPaid ? "#F0FDF4" : crm.brandSoft}
                      color={payment.isPaid ? crm.success : crm.brand}
                      border={`1px solid ${payment.isPaid ? crm.success : crm.border}`}
                      textTransform="none"
                      flexShrink={0}
                      borderRadius="full"
                      px="8px"
                      py="2px"
                      fontSize="xs"
                    >
                      {payment.statusLabel}
                    </Badge>
                  </Flex>
                  {(payment.financialItem.taxaCambio ||
                    payment.financialItem.dataCambio) && (
                    <Text fontSize="xs" color={crm.textFaint} mt={2}>
                      Câmbio:{" "}
                      {payment.financialItem.taxaCambio || "Por definir"}
                      {payment.financialItem.dataCambio &&
                        ` · ${DateTime.fromISO(
                          payment.financialItem.dataCambio,
                        ).toFormat("dd/LL/yyyy")}`}
                    </Text>
                  )}
                  <Button
                    size="sm"
                    mt={3}
                    variant="outline"
                    borderColor={crm.brand}
                    color={crm.brand}
                    borderRadius="8px"
                    _hover={{ bg: crm.brandSoft }}
                    onClick={() => {
                      onClose();
                      onOpenProcess(payment.process);
                    }}
                  >
                    Ver processo completo
                  </Button>
                </Box>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FinancialTimeline;
